// Alkeme-branded theme override for the CMDB Portal.
//
// Backstage's default entity-page header backdrop comes from pageTheme keys
// (`home`, `service`, `apis`, …) — each provides a gradient + an SVG wave
// shape. The defaults use teal/green/red/blue depending on entity kind.
// All of them get overridden here to render with the Alkeme brand navy
// (#25475e -> #022944 gradient) lifted from alkeme-marketing/sites/*/app/
// globals.css. That keeps the entity header consistently on-brand across
// every kind in our catalog (Resource + Group are the only ones in use,
// but we override the whole map for future-proofing).
//
// Palette: also tints AppBar / Primary buttons in brand navy, with the
// gold accent reserved for the sidebar wordmark + warning chips.
//
// Theme is registered via ThemeBlueprint with id='alkeme-light'. Set as
// the default by leaving it as the only registered theme — Backstage's
// app picks the first available when no user preference is stored.

import {
  createUnifiedTheme,
  genPageTheme,
  palettes,
  shapes,
  UnifiedThemeProvider,
} from '@backstage/theme';
import type { AppTheme } from '@backstage/core-plugin-api';
import { ThemeBlueprint } from '@backstage/plugin-app-react';
import { createFrontendModule } from '@backstage/frontend-plugin-api';

// Brand colors lifted verbatim from alkeme-marketing/sites/*/app/globals.css.
const BRAND_NAVY = '#25475e';      // --color-brand
const BRAND_DEEPER = '#022944';    // --color-deeper
const BRAND_GOLD = '#ffbf3b';      // --color-gold
const BRAND_BLUE = '#74a7f5';      // --color-blue (lighter accent)
const BRAND_CREAM = '#f5f1e8';     // --color-cream (page background)

// All entity types use the same Alkeme gradient + wave shape. Backstage
// renders one of these per page type; for our catalog the most-used keys
// are `home` (catalog index) and `service`/`apis`/`other` (entity pages).
const alkemePage = genPageTheme({
  colors: [BRAND_NAVY, BRAND_DEEPER],
  shape: shapes.wave,
});

const alkemeUnifiedTheme = createUnifiedTheme({
  // Start from Backstage's light palette + Alkeme primary/secondary tints.
  palette: {
    ...palettes.light,
    primary: {
      main: BRAND_NAVY,
      dark: BRAND_DEEPER,
      light: BRAND_BLUE,
    },
    secondary: {
      main: BRAND_GOLD,
      dark: '#f0b42e',
    },
    navigation: {
      ...palettes.light.navigation,
      background: BRAND_DEEPER,
      indicator: BRAND_GOLD,
      color: BRAND_CREAM,
      selectedColor: BRAND_GOLD,
    },
  },
  defaultPageTheme: 'home',
  pageTheme: {
    home: alkemePage,
    documentation: alkemePage,
    tool: alkemePage,
    service: alkemePage,
    website: alkemePage,
    library: alkemePage,
    other: alkemePage,
    app: alkemePage,
    apis: alkemePage,
    card: alkemePage,
  },
});

const alkemeTheme: AppTheme = {
  id: 'alkeme-light',
  title: 'Alkeme',
  variant: 'light',
  Provider: ({ children }) => (
    <UnifiedThemeProvider theme={alkemeUnifiedTheme} children={children} />
  ),
};

const alkemeThemeExtension = ThemeBlueprint.make({
  name: 'alkeme-light',
  params: { theme: alkemeTheme },
});

export const alkemeThemeModule = createFrontendModule({
  pluginId: 'app',
  extensions: [alkemeThemeExtension],
});
